import type { ReactNode } from 'react';
import {
  Badge,
  Card,
  CardHeader,
  CardTitle,
  DataTable,
  Stat,
  StatRow,
  StatusDot,
  type ColumnDef,
} from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import type { ApiKey, Customer, CustomerUser, EndpointHealth, StorageClass } from '../types';
import { SHOWN_TO_SUPPORT } from '../words';
import '../admin.css';

const healthTone = {
  healthy: 'success',
  degraded: 'warning',
  down: 'danger',
  idle: 'neutral',
} as const;

const storageWord: Record<StorageClass, string> = {
  originals: 'Originals',
  renders: 'Renders',
  ocr: 'OCR',
  exports: 'Exports',
};

function userColumns(f: Formatter): ColumnDef<CustomerUser, unknown>[] {
  return [
    { accessorKey: 'name', header: 'Name' },
    { accessorKey: 'email', header: 'Email' },
    {
      accessorKey: 'role',
      header: 'Role',
      cell: (c) => <Badge tone="neutral">{c.row.original.role}</Badge>,
    },
    {
      accessorKey: 'lastSeenAt',
      header: 'Last seen',
      cell: (c) => f.ago(c.row.original.lastSeenAt),
    },
  ];
}

function keyColumns(f: Formatter): ColumnDef<ApiKey, unknown>[] {
  return [
    { accessorKey: 'label', header: 'Key' },
    {
      accessorKey: 'prefix',
      header: 'Prefix',
      cell: (c) => <code className="ad-small">{c.row.original.prefix}</code>,
    },
    { accessorKey: 'jobId', header: 'Job', cell: (c) => c.row.original.jobId ?? 'any' },
    {
      accessorKey: 'requests24h',
      header: 'Requests, 24 h',
      cell: (c) => f.int(c.row.original.requests24h),
    },
    {
      accessorKey: 'lastUsedAt',
      header: 'Last used',
      cell: (c) => f.ago(c.row.original.lastUsedAt),
    },
  ];
}

/** Users and roles; null is a staff role that does not read them, said in one line. */
export function UsersPanel({ users }: { users: CustomerUser[] | null }): ReactNode {
  const f = useFormat();
  return (
    <Card>
      <CardHeader>
        <CardTitle>Users</CardTitle>
      </CardHeader>
      {users === null ? (
        <p className="ad-small pui-muted">{SHOWN_TO_SUPPORT}</p>
      ) : (
        <DataTable
          caption="Users and roles"
          columns={userColumns(f)}
          data={users}
          dense
          getRowId={(u) => u.id}
        />
      )}
    </Card>
  );
}

/** API keys and their usage. */
export function KeysPanel({ keys }: { keys: ApiKey[] }): ReactNode {
  const f = useFormat();
  return (
    <Card>
      <CardHeader>
        <CardTitle>API keys</CardTitle>
      </CardHeader>
      <DataTable
        caption="API keys"
        columns={keyColumns(f)}
        data={keys}
        dense
        getRowId={(k) => k.id}
      />
    </Card>
  );
}

/** Storage by class, from `ops.tenant_usage`. */
export function StoragePanel({ storage }: { storage: Customer['storage'] }): ReactNode {
  const f = useFormat();
  const total = storage.reduce((s, x) => s + x.gb, 0);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Storage</CardTitle>
        <span className="pui-muted ad-small">{f.storage(total)} total</span>
      </CardHeader>
      <StatRow>
        {storage.map((s) => (
          <Stat key={s.class} size="sm" value={f.storage(s.gb)} label={storageWord[s.class]} />
        ))}
      </StatRow>
    </Card>
  );
}

/** Intake sources and destinations with health, as support sees them. */
export function EndpointsPanel({
  title,
  endpoints,
  empty,
}: {
  title: string;
  endpoints: EndpointHealth[];
  /** What it means that there are none, in a sentence; a titled empty box says nothing. */
  empty?: string;
}) {
  const f = useFormat();
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      {endpoints.length === 0 && empty ? <p className="ad-small pui-muted">{empty}</p> : null}
      <ul className="ad-list">
        {endpoints.map((e) => (
          <li key={e.id} className="ad-row ad-row--between">
            <span className="ad-row">
              <StatusDot tone={healthTone[e.health]} label={e.health} size="sm" />
              {e.label ? <span>{e.label}</span> : null}
              <Badge tone="neutral">{e.kind}</Badge>
            </span>
            <span className="pui-muted ad-small">{f.ago(e.lastActivityAt)}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
